/**
 * Copyright 2026 Salesforce, Inc.
 *
 * Licensed under the Apache License, Version 2.0 (the "License");
 * you may not use this file except in compliance with the License.
 * You may obtain a copy of the License at
 *
 *     http://www.apache.org/licenses/LICENSE-2.0
 *
 * Unless required by applicable law or agreed to in writing, software
 * distributed under the License is distributed on an "AS IS" BASIS,
 * WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
 * See the License for the specific language governing permissions and
 * limitations under the License.
 */

import type { Image } from '@/types';
import { AttributeDefinition, Component, RegionDefinition } from '@/lib/decorators';
import EditorialProductCarousel, {
    type EditorialProductCarouselData,
} from '@/components/home/editorial-product-carousel';

// oxlint-disable-next-line react/only-export-components
export { fallback } from '@/components/home/editorial-product-carousel';
// oxlint-disable-next-line react/only-export-components
export { loader } from './loaders';

/* v8 ignore start */
@Component('customImageCarousel', {
    name: 'Custom Image Carousel',
    description:
        'Editorial copy paired with up to 6 products. Each product uses an authored campaign image with its live catalog name, price, and product link.',
    group: 'Content',
})
@RegionDefinition([])
// oxlint-disable-next-line react/only-export-components
export class CustomImageCarouselMetadata {
    @AttributeDefinition({ id: 'eyebrow', name: 'Eyebrow', type: 'string' })
    eyebrow?: string;

    @AttributeDefinition({ id: 'heading', name: 'Heading', type: 'string' })
    heading?: string;

    @AttributeDefinition({ id: 'bodyCopy', name: 'Body Copy', type: 'text' })
    bodyCopy?: string;

    @AttributeDefinition({ id: 'ctaText', name: 'CTA Text', type: 'string' })
    ctaText?: string;

    @AttributeDefinition({ id: 'ctaLink', name: 'CTA Link', type: 'url' })
    ctaLink?: string;

    @AttributeDefinition({ id: 'product1Id', name: 'Product 1', type: 'product' })
    product1Id?: string;
    @AttributeDefinition({ id: 'product1Image', name: 'Product 1 Image', type: 'image' })
    product1Image?: Image;

    @AttributeDefinition({ id: 'product2Id', name: 'Product 2', type: 'product' })
    product2Id?: string;
    @AttributeDefinition({ id: 'product2Image', name: 'Product 2 Image', type: 'image' })
    product2Image?: Image;

    @AttributeDefinition({ id: 'product3Id', name: 'Product 3', type: 'product' })
    product3Id?: string;
    @AttributeDefinition({ id: 'product3Image', name: 'Product 3 Image', type: 'image' })
    product3Image?: Image;

    @AttributeDefinition({ id: 'product4Id', name: 'Product 4', type: 'product' })
    product4Id?: string;
    @AttributeDefinition({ id: 'product4Image', name: 'Product 4 Image', type: 'image' })
    product4Image?: Image;

    @AttributeDefinition({ id: 'product5Id', name: 'Product 5', type: 'product' })
    product5Id?: string;
    @AttributeDefinition({ id: 'product5Image', name: 'Product 5 Image', type: 'image' })
    product5Image?: Image;

    @AttributeDefinition({ id: 'product6Id', name: 'Product 6', type: 'product' })
    product6Id?: string;
    @AttributeDefinition({ id: 'product6Image', name: 'Product 6 Image', type: 'image' })
    product6Image?: Image;
}
/* v8 ignore stop */

type ProductReference = string | { id?: string };

export type CustomImageCarouselData = EditorialProductCarouselData;

export interface CustomImageCarouselProps {
    eyebrow?: string;
    heading?: string;
    bodyCopy?: string;
    ctaText?: string;
    ctaLink?: string;
    product1Id?: ProductReference;
    product1Image?: Image;
    product2Id?: ProductReference;
    product2Image?: Image;
    product3Id?: ProductReference;
    product3Image?: Image;
    product4Id?: ProductReference;
    product4Image?: Image;
    product5Id?: ProductReference;
    product5Image?: Image;
    product6Id?: ProductReference;
    product6Image?: Image;
    data?: CustomImageCarouselData | Promise<CustomImageCarouselData> | null;
}

export default function CustomImageCarousel(props: CustomImageCarouselProps) {
    return (
        // RGBARRY-CUSTOMIZATION: match the storefront heading and Category Carousel 1 hover treatments.
        <EditorialProductCarousel
            {...props}
            headingClassName="font-display"
            productImageHoverClassName="duration-700 ease-in-out group-hover:scale-105"
            showNavigationControls
            collapseEditorialWhenEmpty
        />
    );
}
